import { useCallback, useContext, useMemo } from 'react';

import classNames from 'classnames';
import { nanoid } from 'nanoid';
import numeral from 'numeral';
import { AccountContext } from '../context/account';
import { CartBulkItem } from '../core/global';
import {
    getBasketCost,
    getBasketCostForTokens,
    getCostFromTokens
} from '../helpers/cart';
import tableStyles from "../styles/components/table.module.scss";

interface CartPurchaseCostSummaryProps {
  fullTable?: boolean
  columnsPadding?: number
  className?: string
}

export const CartPurchaseCostSummary = ({
  fullTable,
  columnsPadding = 0,
  className,
}: CartPurchaseCostSummaryProps ) => {
  
  const {
    isVatExempt,
    cart,
    paymentType,
    userTokens,
    userCompanyTokens,
    userActivePaymentMethod,
    userPaymentMethods,
  } = useContext(AccountContext);

  const hasPayments = useMemo(() => {
    return !(!userPaymentMethods || (Array.isArray(userPaymentMethods) && userPaymentMethods.length === 0))
  }, [userPaymentMethods])

  const getCost = useCallback((cost: number, tokens: number) => {
    if (paymentType === "card" || paymentType === "po") {
      return `£${numeral(cost).format("0,0.00")}`
    }
    if (paymentType === "tokens") {
      return `${numeral(tokens).format("0,0")} tokens`
    }
    if (!hasPayments) {
      return `N/A`
    }
    return `Free`
  }, [paymentType, hasPayments])

  const content = useCallback( () => {
    const columns = [...new Array(columnsPadding)]
    
    const {
      vatValue, 
      discountPercentage, 
      discountValue, 
      discountTokens,
      vatPercentage, 
      subtotal,
      subtotalTokens,
      total,
      totalTokens,
    } = getBasketCost( cart || [], isVatExempt, paymentType )

    
    if (!hasPayments) {
      return (
        <>
          <tr>
            {columns.map((c, index) => <td key={nanoid()}></td>)}
            <th colSpan={2}>Please <a href="https://www.metoceanworks.com/contact/" target="_blank">contact the MetOceanWorks team</a> for assistance with this purchase.</th>
            {!fullTable && <td></td>}
          </tr>
        </>
      )
    }

    if (paymentType === "subscription") {
      return (
        <>
          <tr>
            {columns.map((c, index) => <td key={nanoid()}></td>)}
            <th colSpan={2}>This is a free data acquisition included in your subscription. Subject to fair use policy.</th>
            {!fullTable && <td></td>}
          </tr>
        </>
      )
    }

    return (
      <>
        <tr>
          {columns.map((c, index) => <td key={nanoid()}></td>)}
          <th>Subtotal:</th>
          <td className={tableStyles.currency}>{getCost(subtotal, subtotalTokens)}</td>
          {!fullTable && <td></td>}
        </tr>
        {discountPercentage > 0 && 
          <tr>
            {columns.map(c => <td key={nanoid()}></td>)}
            <th>Discount @{discountPercentage}%:</th>
            <td className={tableStyles.currency}>-{getCost(discountValue, discountTokens)}</td>
            {!fullTable && <td></td>}
          </tr>
        }
        {paymentType === "tokens" 
          && userTokens 
          && userActivePaymentMethod?.type === "tokens" 
          && discountPercentage === 0 && 
          <tr>
            {columns.map(c => <td key={nanoid()}></td>)}
            <th colSpan={2}>Discount @{userTokens[0].discount_applied_percent}% applied at tokens purchase</th>
            {!fullTable && <td></td>}
          </tr>
        }
        {paymentType === "tokens" 
          && userTokens 
          && userActivePaymentMethod?.type === "company-tokens" 
          && discountPercentage === 0 && 
          <tr>
            {columns.map(c => <td key={nanoid()}></td>)}
            <th colSpan={2}>Discount applied at tokens purchase</th>
            {!fullTable && <td></td>}
          </tr>
        }
        {(paymentType === 'card' || paymentType === 'po') && !isVatExempt && 
          <tr>
            {columns.map(c => <td key={nanoid()}></td>)}
            <th>V.A.T. @{vatPercentage}%:</th>
            <td className={tableStyles.currency}>{getCost(vatValue, 0)}</td>
            {!fullTable && <td></td>}
          </tr>
        }
        <tr>
          {columns.map(c => <td key={nanoid()}></td>)}
          <th>Total:</th>
          <td className={tableStyles.currency}>{getCost(total, totalTokens)}</td>
          {!fullTable && <td></td>}
        </tr>
      </>
    )
  }, [columnsPadding, fullTable, cart, isVatExempt, hasPayments])

  if (fullTable) {
    return (
      <>
        <table className={classNames(className, tableStyles.table)}>
          <tbody>
            {content()}
          </tbody>
        </table>
      </>
    )
  }

  return (
    <>
      {content()}
    </>
  )
}

interface CartPurchaseTokensCostSummaryProps {
  fullTable?: boolean
  columnsPadding?: number
  className?: string
}


export const CartPurchaseTokensCostSummary = ({
  fullTable,
  columnsPadding = 0,
  className,
}: CartPurchaseTokensCostSummaryProps) => {
  
  const {
    refetchCart,
    account,
    isVatExempt,
    cart,
    purchaseTokens,
  } = useContext(AccountContext);

  const content = useCallback( () => {
    const columns = [...new Array(columnsPadding)]

    const {
      vatValue, 
      vatPercentage, 
      subtotal,
      total,
    } = getBasketCostForTokens( purchaseTokens.map((item: CartBulkItem) => {
        const {total} = getCostFromTokens(item)
        return total
      }), isVatExempt )

    return (
      <>
        <tr>
          {columns.map(c => <td key={nanoid()}></td>)}
          <th>Subtotal:</th>
          <td className={tableStyles.currency}>£{numeral(subtotal).format("0,0.00")}</td>
          {!fullTable && <td></td>}
        </tr>
        {!isVatExempt && 
          <tr>
            {columns.map(c => <td key={nanoid()}></td>)}
            <th>V.A.T. @{vatPercentage}%:</th>
            <td className={tableStyles.currency}>£{numeral(vatValue).format("0,0.00")}</td>
            {!fullTable && <td></td>}
          </tr>
        }
        <tr>
          {columns.map(c => <td key={nanoid()}></td>)}
          <th>Total:</th>
          <td className={tableStyles.currency}>£{numeral(total).format("0,0.00")}</td>
          {!fullTable && <td></td>}
        </tr>
      </>
    )
  }, [fullTable, purchaseTokens, isVatExempt, columnsPadding])

  if (fullTable) {
    return (
      <>
        <table className={classNames(className, tableStyles.table)}>
          <tbody>
            {content()}
          </tbody>
        </table>
      </>
    )
  }

  return (
    <>
      {content()}
    </>
  )
}